import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageIcon } from "@/components/PageIcon";
import { TrendingUp, Clock, Truck, Users, PieChart, ScrollText, Download, FileText, UserCheck, Percent, FolderKanban, AlertTriangle, Loader2, Package } from "lucide-react";

const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const REPORTS = [
  { key: "pnl", label: "Management P&L", icon: TrendingUp, sub: "Income against costs, ex VAT", perms: ["finance.profit_loss.view"] },
  { key: "aged-debtors", label: "Aged Debtors", icon: Clock, sub: "Who owes you, by age", perms: ["finance.view"] },
  { key: "aged-creditors", label: "Aged Creditors", icon: Truck, sub: "Unpaid supplier bills, by age", perms: ["finance.view", "expenses.view"] },
  { key: "customer-sales", label: "Customer Sales", icon: Users, sub: "Invoices and till sales per customer", perms: ["finance.view"] },
  { key: "spending-categories", label: "Spending by Category", icon: PieChart, sub: "Approved expenses and bills", perms: ["finance.view", "expenses.view"] },
  { key: "employee-expenses", label: "Employee Expenses", icon: UserCheck, sub: "Claims by person and status", perms: ["finance.view", "expenses.approve"] },
  { key: "vat-summary", label: "Indicative VAT Summary", icon: Percent, sub: "Sales vs purchase VAT (not a return)", perms: ["finance.view"] },
  { key: "project-costs", label: "Project Profitability", icon: FolderKanban, sub: "Income, costs, gross profit and margin per project", perms: ["finance.view", "finance.profit_loss.view"] },
  { key: "audit-trail", label: "Finance Audit Trail", icon: ScrollText, sub: "Who changed what, when", perms: ["finance.manage"] },
];
const fmt = (t, v) => (t === "money" ? money(v) : t === "datetime" ? String(v || "").slice(0, 16).replace("T", " ") : v ?? "—");
const csvCell = (t, v) => { const s = t === "money" ? Number(v || 0).toFixed(2) : String(fmt(t, v)); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

function downloadCsv(d, p) {
  const head = [`${d.title}`, `Period: ${d.period.from || "all records"} to ${d.period.to || "today"}`, ...(d.provisional ? ["PROVISIONAL - based on incomplete data"] : []), ""];
  const lines = [...head, d.columns.map((c) => c[1]).join(","), ...d.rows.map((r) => d.columns.map(([k, , t]) => csvCell(t, r[k])).join(","))];
  if (d.totals && d.rows.length) { lines.push(d.columns.map(([k, , t], i) => (i === 0 ? csvCell("text", d.totals_label) : d.totals[k] !== undefined ? csvCell(t, d.totals[k]) : "")).join(",")); }
  (d.notes || []).forEach((n) => lines.push(csvCell("text", `Note: ${n}`)));
  const url = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${d.key}_${p.date_from || "start"}_to_${p.date_to || new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
  api.post(`/finance/reports/${d.key}/export-log`, { format: "csv", date_from: p.date_from, date_to: p.date_to }).catch(() => {});
}

async function downloadPdf(key, p, setBusy) {
  setBusy(true);
  try {
    const r = await api.get(`/finance/reports/${key}/pdf`, { params: p, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${key}_${p.date_from || "start"}_to_${p.date_to || new Date().toISOString().slice(0, 10)}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Could not create the PDF"); }
  setBusy(false);
}

function Meta({ d }) {
  return (
    <div className="space-y-2 mb-4" data-testid="report-meta">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        {d.provisional && <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 text-amber-800 font-bold px-2 py-1 tracking-wide" data-testid="report-provisional"><AlertTriangle className="w-3.5 h-3.5" /> PROVISIONAL</span>}
        <span data-testid="report-period">Period: <b>{d.period.from || "all records"}</b> to <b>{d.period.to || "today"}</b>{d.as_at ? ` · aged as at ${d.as_at}` : ""}</span>
        {Object.entries(d.completeness || {}).map(([k, v]) => <span key={k} className="rounded-md bg-slate-100 px-2 py-1" data-testid="report-completeness">{k}: <b>{v}</b></span>)}
      </div>
      {d.summary?.length > 0 && <div className="flex flex-wrap gap-2">{d.summary.map(([k, v]) => <div key={k} className="rounded-xl border border-slate-200 px-3 py-2" data-testid="report-summary-item"><div className="text-[11px] text-slate-400">{k}</div><div className="font-bold text-slate-900">{money(v)}</div></div>)}</div>}
    </div>
  );
}

function Drill({ row, onClose }) {
  const recs = row._records || [];
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-2xl" data-testid="report-drilldown">
        <DialogHeader><DialogTitle>{row.section ? `${row.section} · ` : ""}{row.name} — {recs.length} record{recs.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100 text-sm">
          {recs.map((x, i) => (
            <Link key={`${x.id}-${i}`} to={x.link} className="flex items-center gap-3 py-2 hover:bg-slate-50 px-1 rounded" data-testid="report-drill-record">
              <span className="text-xs text-slate-400 w-20 shrink-0">{x.date || "—"}</span>
              <span className="flex-1 min-w-0 truncate">{x.label}</span>
              {x.status && <span className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{x.status.replace("_", " ")}</span>}
              <span className="font-semibold tabular-nums">{money(x.amount)}</span>
            </Link>))}
          {!recs.length && <p className="py-6 text-center text-slate-400">No underlying records</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Table({ d, onDrill }) {
  const cols = d.columns;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-500"><tr>{cols.map(([k, l, t]) => <th key={k} className={`py-2 pr-4 ${t === "money" ? "text-right" : ""}`}>{l}</th>)}</tr></thead>
        <tbody>
          {d.rows.map((r, i) => (
            <tr key={i} onClick={() => r._records && onDrill(r)} className={`border-t border-slate-100 ${r._records ? "cursor-pointer hover:bg-indigo-50/50" : ""}`} data-testid={`report-row-${i}`}>
              {cols.map(([k, , t]) => <td key={k} className={`py-2 pr-4 ${t === "money" ? "text-right whitespace-nowrap tabular-nums" : "max-w-[360px] truncate"} ${k === "90_plus" && r[k] > 0 ? "text-rose-700 font-semibold" : ""}`}>{fmt(t, r[k])}</td>)}
            </tr>))}
          {d.totals && d.rows.length > 0 && <tr className="border-t-2 border-slate-300 font-bold" data-testid="report-totals">{cols.map(([k, , t], i) => <td key={k} className={`py-2 pr-4 ${t === "money" ? "text-right whitespace-nowrap" : ""}`}>{i === 0 ? d.totals_label : d.totals[k] !== undefined ? fmt(t, d.totals[k]) : ""}</td>)}</tr>}
          {!d.rows.length && <tr><td colSpan={cols.length} className="py-8 text-center text-slate-400" data-testid="report-empty">No records in this period</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ReportView({ rep }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [p, setP] = useState({ date_from: "", date_to: "", q: "" });
  const [drill, setDrill] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { setD(null); setErr(""); api.get(`/finance/reports/${rep.key}`, { params: p }).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Could not load this report")); }, [rep.key, p]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`report-${rep.key}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h2 className="text-lg font-bold text-slate-900 mr-auto">{rep.label}</h2>
        <label className="text-xs text-slate-500">From</label><Input type="date" className="w-40" value={p.date_from} onChange={(e) => setP((s) => ({ ...s, date_from: e.target.value }))} data-testid="report-date-from" />
        <label className="text-xs text-slate-500">To</label><Input type="date" className="w-40" value={p.date_to} onChange={(e) => setP((s) => ({ ...s, date_to: e.target.value }))} data-testid="report-date-to" />
        {rep.key === "audit-trail" && <Input className="w-56" placeholder="Search action, detail or person" value={p.q} onChange={(e) => setP((s) => ({ ...s, q: e.target.value }))} data-testid="report-search" />}
        <Button variant="outline" size="sm" className="gap-1.5" disabled={!d} onClick={() => downloadCsv(d, p)} data-testid="report-download-csv"><Download className="w-4 h-4" /> CSV</Button>
        <Button variant="outline" size="sm" className="gap-1.5" disabled={!d || busy} onClick={() => downloadPdf(rep.key, p, setBusy)} data-testid="report-download-pdf">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF</Button>
      </div>
      {err && <p className="py-8 text-center text-rose-600 text-sm" data-testid="report-error">{err}</p>}
      {!d && !err && <p className="py-8 text-center text-slate-400 text-sm">Loading…</p>}
      {d && <><Meta d={d} />{d.rows.some((r) => r._records) && <p className="text-[11px] text-slate-400 mb-1">Click a row to see the records behind it.</p>}<Table d={d} onDrill={setDrill} />
        {d.notes?.length > 0 && <ul className="mt-4 space-y-1 text-xs text-slate-500 list-disc pl-5" data-testid="report-notes">{d.notes.map((n) => <li key={n}>{n}</li>)}</ul>}</>}
      {drill && <Drill row={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

function MonthPack() {
  const last = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
  const [month, setMonth] = useState(`${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}`);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.get("/finance/reports/pack", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `finance_reports_${month}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Month pack downloaded");
    } catch { toast.error("Could not build the month pack"); }
    setBusy(false);
  };
  return (
    <div className="sm:ml-auto flex flex-wrap items-center gap-2 w-full sm:w-auto" data-testid="month-pack">
      <Input type="month" className="w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="month-pack-month" />
      <Button onClick={go} disabled={!month || busy} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="month-pack-download">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />} Download month pack (PDFs)</Button>
    </div>
  );
}

export default function FinanceReportsPage() {
  const { canAny } = useAuth();
  const list = REPORTS.filter((r) => canAny(r.perms));
  const [sel, setSel] = useState(() => new URLSearchParams(window.location.search).get("report"));
  const rep = list.find((r) => r.key === sel);
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="finance-reports-page">
      <div className="flex flex-wrap items-center gap-3 mb-4"><PageIcon />
        <div><h1 className="text-2xl font-bold text-slate-900">Reports</h1><p className="text-sm text-slate-500">Management reports from your invoices, till sales, bills and expenses. Not statutory accounts.</p></div>
        {list.length > 0 && <MonthPack />}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
        {list.map((r) => (
          <button key={r.key} onClick={() => setSel(r.key)} data-testid={`report-card-${r.key}`}
            className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-colors hover:border-indigo-300 ${sel === r.key ? "border-indigo-500 bg-indigo-50" : "border-slate-200 bg-white"}`}>
            <r.icon className="w-5 h-5 text-indigo-500" /><div><div className="font-semibold text-slate-900">{r.label}</div><div className="text-xs text-slate-500">{r.sub}</div></div>
          </button>))}
        {!list.length && <p className="text-sm text-slate-400" data-testid="reports-none">You don't have access to any finance reports.</p>}
      </div>
      {rep && <ReportView key={rep.key} rep={rep} />}
    </div>
  );
}
